/**
 * Pure coordinate parsing utilities extracted from location-search component.
 * Handles DD, DMS, UTM, and MGRS coordinate format parsing and conversion.
 */

export interface CoordinateParseResult {
  coordinates: [number, number]
  format: 'decimal' | 'dms' | 'utm' | 'mgrs'
  bounds?: {
    ne: [number, number]
    sw: [number, number]
  }
}

export function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180)
}

export function calculateDistance(point1: [number, number], point2: [number, number]): number {
  const [lat1, lng1] = point1
  const [lat2, lng2] = point2

  const R = 6371 // Earth's radius in kilometers
  const dLat = toRadians(lat2 - lat1)
  const dLng = toRadians(lng2 - lng1)

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

export function createPointBounds(
  coordinates: [number, number],
  bufferKm: number = 1
): { ne: [number, number]; sw: [number, number] } {
  const [lat, lng] = coordinates
  const latBuffer = bufferKm / 111 // Rough conversion: 1 degree ~ 111 km
  const lngBuffer = bufferKm / (111 * Math.cos((lat * Math.PI) / 180))

  return {
    ne: [lat + latBuffer, lng + lngBuffer],
    sw: [lat - latBuffer, lng - lngBuffer],
  }
}

export function utmToLatLng(
  zone: number,
  letter: string,
  easting: number,
  northing: number
): [number, number] | null {
  // Simplified UTM to lat/lng conversion
  // This is a basic approximation - for production use, consider a proper UTM library
  try {
    const a = 6378137 // WGS84 semi-major axis
    const e = 0.0818191908426 // WGS84 first eccentricity
    const k0 = 0.9996 // UTM scale factor

    // Determine hemisphere
    const northern = letter >= 'N'
    if (!northern) {
      northing = 10000000 - northing // Adjust for southern hemisphere
    }

    // Central meridian for the zone
    const lonOrigin = (zone - 1) * 6 - 180 + 3

    // Remove false easting
    const x = easting - 500000
    const y = northing

    // Simplified calculation (this is an approximation)
    const M = y / k0
    const mu = M / (a * (1 - (e * e) / 4 - (3 * e * e * e * e) / 64))

    // More precise calculation would require additional terms
    const lat = mu + Math.sin(2 * mu) * ((e * e) / 2 + (5 * e * e * e * e) / 24)
    const lng = lonOrigin + (x / k0 / (a * Math.cos(lat))) * (180 / Math.PI)

    const latDeg = lat * (180 / Math.PI)

    if (latDeg >= -90 && latDeg <= 90 && lng >= -180 && lng <= 180) {
      return [latDeg, lng]
    }
  } catch {
    // UTM parsing error
  }
  return null
}

export function mgrsToLatLng(
  zone: number,
  latBand: string,
  gridSquare: string,
  coords: string
): [number, number] | null {
  // Simplified MGRS to lat/lng conversion
  // This is a basic approximation - for production use, consider a proper MGRS library
  try {
    // Extract easting and northing from coordinate string
    const coordLen = coords.length
    if (coordLen % 2 !== 0) {
      return null
    }

    const precision = coordLen / 2
    const easting = parseInt(coords.substring(0, precision)) * Math.pow(10, 5 - precision)
    const northing = parseInt(coords.substring(precision)) * Math.pow(10, 5 - precision)

    // Basic grid square to UTM approximation (simplified)
    const gridEasting = (gridSquare.charCodeAt(0) - 'A'.charCodeAt(0)) * 100000
    const gridNorthing = (gridSquare.charCodeAt(1) - 'A'.charCodeAt(0)) * 100000

    const utmEasting = gridEasting + easting
    const utmNorthing = gridNorthing + northing

    return utmToLatLng(zone, latBand, utmEasting, utmNorthing)
  } catch {
    // MGRS parsing error
  }
  return null
}

export function parseCoordinates(input: string): CoordinateParseResult | null {
  const cleanInput = input.trim()

  // Decimal degrees: 45.123, -122.456 or 45.123 -122.456
  const decimalRegex = /^(-?\d+\.?\d*)[,\s]+(-?\d+\.?\d*)$/
  const decimalMatch = cleanInput.match(decimalRegex)
  if (decimalMatch) {
    const lat = parseFloat(decimalMatch[1])
    const lng = parseFloat(decimalMatch[2])

    if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      return {
        coordinates: [lat, lng],
        format: 'decimal',
        bounds: createPointBounds([lat, lng]),
      }
    }
  }

  // DMS: 45°30'15"N 122°30'15"W
  const dmsRegex = /(\d+)°(\d+)'(\d+)"([NS])\s+(\d+)°(\d+)'(\d+)"([EW])/i
  const dmsMatch = cleanInput.match(dmsRegex)
  if (dmsMatch) {
    const latDeg = parseInt(dmsMatch[1])
    const latMin = parseInt(dmsMatch[2])
    const latSec = parseInt(dmsMatch[3])
    const latDir = dmsMatch[4].toUpperCase()

    const lngDeg = parseInt(dmsMatch[5])
    const lngMin = parseInt(dmsMatch[6])
    const lngSec = parseInt(dmsMatch[7])
    const lngDir = dmsMatch[8].toUpperCase()

    let lat = latDeg + latMin / 60 + latSec / 3600
    let lng = lngDeg + lngMin / 60 + lngSec / 3600

    if (latDir === 'S') {
      lat = -lat
    }
    if (lngDir === 'W') {
      lng = -lng
    }

    if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      return {
        coordinates: [lat, lng],
        format: 'dms',
        bounds: createPointBounds([lat, lng]),
      }
    }
  }

  // UTM: 10T 594934 4282182 (zone letter easting northing)
  const utmRegex = /^(\d{1,2})([A-Z])\s+(\d+)\s+(\d+)$/i
  const utmMatch = cleanInput.match(utmRegex)
  if (utmMatch) {
    const zone = parseInt(utmMatch[1])
    const letter = utmMatch[2].toUpperCase()
    const easting = parseInt(utmMatch[3])
    const northing = parseInt(utmMatch[4])

    const result = utmToLatLng(zone, letter, easting, northing)
    if (result) {
      return {
        coordinates: result,
        format: 'utm',
        bounds: createPointBounds(result, 0.1),
      }
    }
  }

  // MGRS: 10TEJ9493482182 (basic pattern - simplified for common cases)
  const mgrsRegex = /^(\d{1,2})([A-Z])([A-Z]{2})(\d{2,10})$/i
  const mgrsMatch = cleanInput.match(mgrsRegex)
  if (mgrsMatch) {
    const zone = parseInt(mgrsMatch[1])
    const latBand = mgrsMatch[2].toUpperCase()
    const gridSquare = mgrsMatch[3].toUpperCase()
    const coords = mgrsMatch[4]

    const result = mgrsToLatLng(zone, latBand, gridSquare, coords)
    if (result) {
      return {
        coordinates: result,
        format: 'mgrs',
        bounds: createPointBounds(result, 0.1),
      }
    }
  }

  return null
}
